import { DotsThreeVerticalIcon } from '@phosphor-icons/react';
import { css, html } from 'react-strict-dom';
import { Api } from 'src/api';
import {
  DropdownMenu,
  DropdownMenuItem,
  MaybeLink,
  Text,
  TimerCompanyLogo,
} from 'src/components';
import { Color, Style } from 'src/constants';
import { color } from 'src/constants/color.css';
import { Model } from 'src/models';
import { formatTimeSpent } from 'src/util';

type Props = {
  workDone: Model.WorkDone.Logged<Api.WorkDone.V_List>;
  onEdit: () => void;
  onDelete: () => void;
};

/**
 * One banked entry on the timesheet.  Edit and delete sit behind a menu rather
 * than appearing on hover, so the row works the same under a finger.
 */
export default function TimeLogEntryRow({ workDone, onEdit, onDelete }: Props) {
  const target = Model.TimerTarget.fromWorkDone(workDone);
  const category = workDone.categoryString;

  return (
    <html.div style={[Style.row, Style.alignCenter, styles.row]}>
      <TimerCompanyLogo
        company={target?.company ?? null}
        title={target?.title ?? 'Untitled work'}
      />

      <html.div style={[Style.col, styles.label]}>
        <html.div style={[Style.row, Style.alignCenter, styles.nameRow]}>
          <MaybeLink to={target?.path ?? null}>
            <Text typeface="body.small.medium" color="neutral100">
              {target?.title ?? 'Untitled work'}
            </Text>
          </MaybeLink>
          {target?.reference && (
            <Text typeface="caption.medium" color="neutral60">
              {target.reference}
            </Text>
          )}
        </html.div>
        <Text typeface="caption.medium" color="neutral60">
          {getMeta(target)}
        </Text>
      </html.div>

      {/* Only a client or a project carries one; the rest are specific enough
          to say what the time was for on their own. */}
      {category && (
        <html.span style={styles.category}>
          <Text typeface="caption.medium" color="neutral80">
            {Model.WorkDoneCategory.format(category)}
          </Text>
        </html.span>
      )}

      <html.span style={styles.duration}>
        <Text typeface="body.small.medium" color="neutral100" textAlign="right">
          {formatTimeSpent(workDone.minutes) ?? '--'}
        </Text>
      </html.span>

      <DropdownMenu
        anchorSide="right"
        trigger={
          <html.span style={[Style.centerBoth, styles.menuButton]}>
            <DotsThreeVerticalIcon size={18} color={Color.neutral80} />
          </html.span>
        }
      >
        {(close) => (
          <>
            <DropdownMenuItem
              showRadio={false}
              onClick={() => {
                close();
                onEdit();
              }}
            >
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem
              showRadio={false}
              onClick={() => {
                close();
                onDelete();
              }}
            >
              Delete
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenu>
    </html.div>
  );
}

function getMeta(target: ReturnType<typeof Model.TimerTarget.fromWorkDone>) {
  if (!target) return 'No longer tied to any work';

  const kind = {
    company: 'Client',
    project: 'Project',
    serviceOrder: 'Service order',
    ticket: 'Ticket',
    task: 'Task',
  }[target.kind];

  return target.company && target.kind !== 'company'
    ? `${target.company.name} · ${kind}`
    : kind;
}

const styles = css.create({
  row: {
    gap: 12,
    paddingBlock: 10,
    paddingLeft: 16,
    paddingRight: 12,
  },
  label: {
    flexGrow: 1,
    minWidth: 0,
    gap: 2,
  },
  nameRow: {
    gap: 6,
    minWidth: 0,
  },
  category: {
    flexShrink: 0,
    paddingInline: 8,
    paddingBlock: 2,
    borderRadius: 999,
    backgroundColor: color.neutral10,
  },
  duration: {
    flexShrink: 0,
    width: 64,
    display: 'flex',
    justifyContent: 'flex-end',
  },
  menuButton: {
    flexShrink: 0,
    width: 26,
    height: 26,
    borderRadius: 6,
    cursor: 'pointer',
    ':hover': {
      backgroundColor: color.neutral10,
    },
  },
});
